import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, deleteDoc, doc, updateDoc, setDoc, orderBy, query, getFirestore, Timestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import UserSubscriptionModal from './UserSubscriptionModal';
import { API_ENDPOINTS } from '../config/api';

/* --- Improved Email Users UI Styles --- */
const emailUsersStyles = `
.improved-ui {
  display: flex;
  flex-wrap: wrap;
  gap: 2.5rem;
  align-items: flex-start;
  justify-content: flex-start;
}
.card {
  background: var(--surface-color);
  border-radius: 24px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04), 0 1px 4px rgba(0, 0, 0, 0.02);
  border: 1px solid var(--outline-color);
  padding: 2rem;
  min-width: 340px;
  max-width: 420px;
  width: 100%;
  backdrop-filter: blur(20px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.card:hover {
  box-shadow: 0 12px 32px rgba(139, 92, 246, 0.12), 0 4px 12px rgba(0, 0, 0, 0.06);
  transform: translateY(-2px);
}
.form-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: var(--on-background);
  letter-spacing: -0.5px;
}
.form-section {
  margin-bottom: 1.3rem;
}
.form-label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--on-background);
  font-size: 0.95rem;
}
.form-select, .form-input, .form-textarea {
  width: 100%;
  padding: 0.875rem 1.25rem;
  border-radius: 16px;
  border: 1px solid var(--outline-color);
  font-size: 1rem;
  margin-top: 0.5rem;
  background: var(--surface-variant);
  color: var(--on-background);
  outline: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: inherit;
}
.form-select:focus, .form-input:focus, .form-textarea:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}
.send-btn {
  background: linear-gradient(90deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  color: #fff;
  font-weight: 700;
  border: none;
  border-radius: 16px;
  padding: 1rem 1.75rem;
  font-size: 1.05rem;
  cursor: pointer;
  margin-top: 1rem;
  box-shadow: 0 4px 16px rgba(139, 92, 246, 0.2);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
}
.send-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(139, 92, 246, 0.3);
}
.send-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.email-list-box {
  background: var(--surface-variant);
  border-radius: 16px;
  padding: 1rem;
  margin-bottom: 0.5rem;
  border: 1px solid var(--outline-color);
}
.email-list-scroll {
  max-height: 140px;
  overflow-y: auto;
  margin-top: 0.5rem;
  padding-right: 0.5rem;
}
.email-checkbox-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.98rem;
  color: var(--on-background);
  cursor: pointer;
}
.select-all-label {
  font-weight: 600;
  color: var(--primary-color);
  margin-bottom: 0.75rem;
  display: block;
  cursor: pointer;
  font-size: 0.95rem;
  transition: color 0.2s;
}
.select-all-label:hover {
  color: var(--secondary-color);
}
.empty-list {
  color: var(--on-surface-variant);
  font-style: italic;
  padding: 0.7rem 0;
  opacity: 0.7;
}
.preview-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--on-background);
  margin-bottom: 1rem;
  letter-spacing: -0.3px;
}
.email-send-result.success {
  color: #2ecc71;
  margin-top: 1rem;
  font-weight: 600;
}
.email-send-result.error {
  color: #e74c3c;
  margin-top: 1rem;
  font-weight: 600;
}
@media (max-width: 900px) {
  .improved-ui {
    flex-direction: column;
    gap: 1.5rem;
    align-items: stretch;
  }
  .card {
    min-width: unset;
    max-width: unset;
    width: 100%;
  }
}
.email-chips-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.6rem 0.7rem;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  max-height: 180px;
  overflow-y: auto;
}
.email-chip {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: var(--surface-variant);
  border: 1px solid var(--outline-color);
  border-radius: 16px;
  padding: 0.5rem 1rem 0.5rem 0.75rem;
  font-size: 0.95rem;
  color: var(--on-background);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.02);
  user-select: none;
}
.email-chip:hover {
  background: var(--surface-color);
  border-color: var(--primary-color);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.1);
}
.email-chip.selected {
  background: linear-gradient(90deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  color: #fff;
  border-color: var(--primary-color);
  box-shadow: 0 4px 16px rgba(139, 92, 246, 0.2);
  transform: translateY(-1px);
}
.chip-avatar {
  background: rgba(255, 255, 255, 0.2);
  color: inherit;
  font-weight: 700;
  border-radius: 12px;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;
}
.email-chip:hover .chip-avatar {
  transform: scale(1.1);
}
.chip-email {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sent-emails-table table {
  width: 100%;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 2px 8px rgba(99,102,241,0.04);
}
.sent-row:hover {
  background: #ece7ff;
}
.sent-detail-modal {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(60, 51, 93, 0.18);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sent-detail-content {
  max-width: 480px;
  width: 100%;
  position: relative;
  padding: 2.2rem 2rem 2rem 2rem;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 8px 32px rgba(99,102,241,0.13);
}
.close-btn {
  position: absolute;
  top: 1.1rem;
  right: 1.1rem;
  background: none;
  border: none;
  font-size: 2rem;
  color: #c490d1;
  cursor: pointer;
  font-weight: 700;
  line-height: 1;
  transition: color 0.18s;
}
.close-btn:hover {
  color: #3d335d;
}
.email-chips-scrollbox {
  background: #f9f7fd;
  border-radius: 10px;
  border: 1.5px solid #ece7ff;
  max-height: 260px;
  overflow-y: auto;
  margin-bottom: 0.5rem;
  padding: 0.7rem 0.5rem 0.7rem 0.5rem;
}
.custom-select-wrapper {
  position: relative;
  width: 100%;
  margin-top: 0.3rem;
}
.custom-select {
  background: #f9f7fd;
  border: 1.5px solid #ece7ff;
  border-radius: 8px;
  padding: 0.7rem 2.2rem 0.7rem 0.9rem;
  font-size: 1rem;
  color: #3d335d;
  cursor: pointer;
  position: relative;
  transition: border 0.2s;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.custom-select:focus, .custom-select-wrapper:focus-within .custom-select {
  border: 1.5px solid #c490d1;
}
.custom-select-chevron {
  position: absolute;
  right: 1.1rem;
  pointer-events: none;
  font-size: 1.1rem;
  color: #b4abc8;
}
.custom-select-dropdown {
  position: absolute;
  left: 0; right: 0;
  top: 110%;
  background: #fff;
  border: 1.5px solid #ece7ff;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(99,102,241,0.09);
  z-index: 10;
  margin-top: 0.2rem;
  padding: 0.2rem 0;
}
.custom-select-option {
  padding: 0.7rem 1.1rem;
  font-size: 1rem;
  color: #3d335d;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.custom-select-option.selected, .custom-select-option:hover {
  background: #ece7ff;
  color: #c490d1;
}

/* Support Requests & Users Styles - Modern Design */
.support-requests-container, .users-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.search-filter-bar {
  display: flex;
  gap: 1rem;
  align-items: center;
  background: var(--surface-color);
  padding: 1.5rem;
  border-radius: 24px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04), 0 1px 4px rgba(0, 0, 0, 0.02);
  border: 1px solid var(--outline-color);
  backdrop-filter: blur(20px);
}

.search-input {
  flex: 1;
  padding: 0.875rem 1.25rem;
  border: 1px solid var(--outline-color);
  border-radius: 16px;
  font-size: 1rem;
  background: var(--surface-variant);
  color: var(--on-background);
  outline: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: inherit;
}

.search-input:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.status-filter {
  padding: 0.875rem 1.25rem;
  border: 1px solid var(--outline-color);
  border-radius: 16px;
  background: var(--surface-variant);
  color: var(--on-background);
  font-size: 1rem;
  cursor: pointer;
  outline: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: inherit;
}

.status-filter:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.requests-grid, .users-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 1.5rem;
}

.request-card, .user-card {
  background: var(--surface-color);
  border-radius: 24px;
  padding: 1.75rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04), 0 1px 4px rgba(0, 0, 0, 0.02);
  border: 1px solid var(--outline-color);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  backdrop-filter: blur(20px);
  position: relative;
  overflow: hidden;
}

.request-card::before, .user-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
  opacity: 0;
  transition: opacity 0.3s;
}

.request-card:hover, .user-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(139, 92, 246, 0.12), 0 4px 12px rgba(0, 0, 0, 0.06);
  border-color: var(--primary-color);
}

.request-card:hover::before, .user-card:hover::before {
  opacity: 1;
}

.request-header, .user-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.request-avatar, .user-avatar {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 18px;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: 1.25rem;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.2);
  transition: transform 0.2s;
}

.request-card:hover .request-avatar, .user-card:hover .user-avatar {
  transform: scale(1.05);
}

.request-info, .user-info {
  flex: 1;
}

.request-name, .user-name {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--on-background);
  margin-bottom: 0.25rem;
  letter-spacing: -0.3px;
}

.request-email, .user-email {
  font-size: 0.9rem;
  color: var(--on-surface-variant);
  margin-bottom: 0.5rem;
}

.request-category, .user-created {
  display: inline-block;
  padding: 0.4rem 0.875rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  background: rgba(139, 92, 246, 0.1);
  color: var(--primary-color);
  border: 1px solid rgba(139, 92, 246, 0.2);
}

.request-status {
  display: inline-block;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-left: 0.5rem;
}

.request-status.pending {
  background: rgba(245, 158, 11, 0.1);
  color: var(--warning-color);
  border: 1px solid rgba(245, 158, 11, 0.2);
}

.request-status.resolved {
  background: rgba(16, 185, 129, 0.1);
  color: var(--success-color);
  border: 1px solid rgba(16, 185, 129, 0.2);
}

.request-status.in-progress {
  background: rgba(6, 182, 212, 0.1);
  color: var(--tertiary-color);
  border: 1px solid rgba(6, 182, 212, 0.2);
}

.request-subject {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--on-background);
  margin-bottom: 0.75rem;
  letter-spacing: -0.2px;
}

.request-message {
  font-size: 0.9rem;
  color: var(--on-surface-variant);
  line-height: 1.6;
  margin-bottom: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.request-meta, .user-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
  color: #b4abc8;
}

.request-date, .user-date {
  font-weight: 500;
}

.request-actions {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  padding: 0.4rem 0.8rem;
  border: none;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.view-btn {
  background: rgba(139, 92, 246, 0.1);
  color: var(--primary-color);
  border: 1px solid rgba(139, 92, 246, 0.2);
  transition: all 0.2s;
}

.view-btn:hover {
  background: var(--primary-color);
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.2);
}

.status-btn {
  background: var(--surface-variant);
  color: var(--on-surface-variant);
  border: 1px solid var(--outline-color);
  transition: all 0.2s;
}

.status-btn:hover {
  background: var(--surface-color);
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.support-detail-modal {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(26, 11, 46, 0.4);
  backdrop-filter: blur(8px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  animation: fadeIn 0.2s;
}

.support-detail-content {
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  background: var(--surface-color);
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 24px rgba(139, 92, 246, 0.12);
  border: 1px solid var(--outline-color);
  position: relative;
  backdrop-filter: blur(20px);
  animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.support-detail-header {
  padding: 2rem 2rem 1rem 2rem;
  border-bottom: 1px solid var(--outline-color);
  position: sticky;
  top: 0;
  background: var(--surface-color);
  border-radius: 24px 24px 0 0;
  backdrop-filter: blur(20px);
}

.support-detail-body {
  padding: 1.5rem 2rem 2rem 2rem;
}

.support-detail-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--on-background);
  margin-bottom: 0.75rem;
  letter-spacing: -0.5px;
}

.support-detail-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
}

.support-detail-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: var(--on-surface-variant);
}

.support-detail-label {
  font-weight: 600;
  color: var(--on-background);
}

.support-detail-message {
  background: var(--surface-variant);
  border: 1px solid var(--outline-color);
  border-radius: 20px;
  padding: 1.5rem;
  margin-top: 1rem;
  white-space: pre-line;
  line-height: 1.7;
  color: var(--on-background);
  font-size: 0.95rem;
}

.user-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.user-detail-item {
  background: var(--surface-variant);
  border: 1px solid var(--outline-color);
  border-radius: 16px;
  padding: 1.25rem;
  transition: all 0.2s;
}

.user-detail-item:hover {
  border-color: var(--primary-color);
  transform: translateY(-2px);
}

.user-detail-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--on-surface-variant);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.user-detail-value {
  font-size: 1rem;
  color: var(--on-background);
  font-weight: 600;
}

.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: var(--on-surface-variant);
  background: var(--surface-color);
  border-radius: 24px;
  border: 1px dashed var(--outline-color);
}

.empty-state-icon {
  font-size: 4rem;
  margin-bottom: 1.5rem;
  opacity: 0.4;
  filter: grayscale(0.3);
}

.empty-state-text {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: var(--on-background);
}

.empty-state-subtext {
  font-size: 0.95rem;
  opacity: 0.7;
  color: var(--on-surface-variant);
}

@media (max-width: 768px) {
  .search-filter-bar {
    flex-direction: column;
    align-items: stretch;
  }
  
  .requests-grid, .users-grid {
    grid-template-columns: 1fr;
  }
  
  .support-detail-modal {
    padding: 1rem;
  }
  
  .support-detail-content {
    max-height: 95vh;
  }
}
`;
if (typeof document !== 'undefined' && !document.getElementById('email-users-styles')) {
  const style = document.createElement('style');
  style.id = 'email-users-styles';
  style.innerHTML = emailUsersStyles;
  document.head.appendChild(style);
}

if (typeof document !== 'undefined' && !document.getElementById('user-detail-modal-styles')) {
  const style = document.createElement('style');
  style.id = 'user-detail-modal-styles';
  style.innerHTML = `
    .user-detail-modal {
      position: fixed;
      top: 0; right: 0; bottom: 0; left: 0;
      background: rgba(26, 11, 46, 0.4);
      backdrop-filter: blur(8px);
      z-index: 1000;
      display: flex;
      justify-content: flex-end;
      align-items: stretch;
      transition: background 0.2s;
      animation: fadeIn 0.2s;
    }
    .user-detail-content {
      width: 400px;
      max-width: 90vw;
      height: 100%;
      background: var(--surface-color);
      border-radius: 24px 0 0 24px;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 24px rgba(139, 92, 246, 0.12);
      border: 1px solid var(--outline-color);
      position: relative;
      padding: 2rem;
      overflow-y: auto;
      transform: translateX(100%);
      animation: slideInRight 0.35s cubic-bezier(.4,0,.2,1) forwards;
      backdrop-filter: blur(20px);
    }
    @keyframes slideInRight {
      from { transform: translateX(100%); }
      to   { transform: translateX(0); }
    }
    .close-btn {
  position: absolute; top: 1.25rem; right: 1.25rem;
  background: rgba(139, 92, 246, 0.1); border: none; font-size: 1.75rem;
  color: var(--primary-color); cursor: pointer; font-weight: 700; line-height: 1;
  width: 2.5rem; height: 2.5rem; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.2s;
}
.close-btn:hover {
  background: var(--primary-color);
  color: #fff;
  transform: rotate(90deg);
    }
  `;
  document.head.appendChild(style);
}

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('waitlist');
  const [waitlist, setWaitlist] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [supportRequests, setSupportRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [featureCounts, setFeatureCounts] = useState([]);
  const [emailForm, setEmailForm] = useState({
    group: 'waitlist',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [emailList, setEmailList] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [selectAll, setSelectAll] = useState(true);
  const [emailSearch, setEmailSearch] = useState('');
  const [sentEmails, setSentEmails] = useState([]);
  const [showSentDetail, setShowSentDetail] = useState(null);
  const [showSupportDetail, setShowSupportDetail] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [supportSearch, setSupportSearch] = useState('');
  const [supportStatusFilter, setSupportStatusFilter] = useState('all');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const navigate = useNavigate();
  const [showResolved, setShowResolved] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [subscriptionSearch, setSubscriptionSearch] = useState('');
  const [searchedUser, setSearchedUser] = useState(null);
  const [promoCodes, setPromoCodes] = useState([]);
  const [newPromoCode, setNewPromoCode] = useState({ code: '', discountType: 'free', value: 30, expiresAt: '', maxUses: 0 });
  const [updatingPremium, setUpdatingPremium] = useState(false);
  const [creatingPromo, setCreatingPromo] = useState(false);
  const [premiumFilter, setPremiumFilter] = useState('all');
  
  // Enhanced filtering states
  const [userPremiumFilter, setUserPremiumFilter] = useState('all');
  const [userDateFilter, setUserDateFilter] = useState({ start: '', end: '' });
  const [userSortBy, setUserSortBy] = useState('createdAt');
  const [userSortOrder, setUserSortOrder] = useState('desc');
  const [subscriptionTypeFilter, setSubscriptionTypeFilter] = useState('all');
  const [subscriptionExpiryFilter, setSubscriptionExpiryFilter] = useState('all');
  const [subscriptionSortBy, setSubscriptionSortBy] = useState('email');
  const [subscriptionSortOrder, setSubscriptionSortOrder] = useState('asc');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const emailTypeOptions = [
    { value: 'waitlist', label: 'Waitlist' },
    { value: 'newsletter', label: 'Newsletter' },
    { value: 'app_users', label: 'App Users' },
    { value: 'all', label: 'All' },
  ];

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) navigate('/login');
    });
    fetchData();
    return () => unsubscribe();
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const waitlistQuery = query(collection(db, 'waitlist'), orderBy('timestamp', 'desc'));
      const waitlistSnap = await getDocs(waitlistQuery);
      const waitlistData = waitlistSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setWaitlist(waitlistData);
      const newsletterQuery = query(collection(db, 'newsletter'), orderBy('timestamp', 'desc'));
      const newsletterSnap = await getDocs(newsletterQuery);
      setNewsletter(newsletterSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      
      // Fetch support requests
      const supportQuery = query(collection(db, 'support_requests'), orderBy('createdAt', 'desc'));
      const supportSnap = await getDocs(supportQuery);
      const supportRequestsData = supportSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSupportRequests(supportRequestsData);
      
      // Fetch all user profiles from any subcollection named 'users'
      const usersSnap = await getDocs(collection(db, 'users'));
      const usersData = usersSnap.docs.map(doc => {
        const data = doc.data();
        const { availableBalance, ...rest } = data;
        return { id: doc.id, ...rest };
      });
      setUsers(usersData);
      
      // Fetch promo codes
      const promoSnap = await getDocs(collection(db, 'promo_codes'));
      setPromoCodes(promoSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      
      // Features aggregation
      const featureMap = {};
      waitlistData.forEach(entry => {
        if (Array.isArray(entry.features)) {
          entry.features.forEach(f => {
            const feature = f.trim();
            if (feature) featureMap[feature] = (featureMap[feature] || 0) + 1;
          });
        }
      });
      // Convert to sorted array
      const sortedFeatures = Object.entries(featureMap)
        .map(([feature, count]) => ({ feature, count }))
        .sort((a, b) => b.count - a.count);
      setFeatureCounts(sortedFeatures);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
    setLoading(false);
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    await deleteDoc(doc(db, type, id));
    fetchData();
  };

  const handleEdit = (entry) => {
    setEditId(entry.id);
    setEditData(entry);
  };

  const handleEditChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const handleEditSave = async (type) => {
    await updateDoc(doc(db, type, editId), editData);
    setEditId(null);
    setEditData({});
    fetchData();
  };

  const handleLogout = async () => {
    await auth.signOut();
    navigate('/login');
  };

  // Update support request status
  const updateSupportStatus = async (requestId, newStatus) => {
    setUpdatingStatus(true);
    try {
      await updateDoc(doc(db, 'support_requests', requestId), {
        status: newStatus,
        updatedAt: new Date()
      });
      
      // Update local state
      setSupportRequests(prev => 
        prev.map(request => 
          request.id === requestId 
            ? { ...request, status: newStatus, updatedAt: new Date() }
            : request
        )
      );
      
      // Update detail modal if open
      if (showSupportDetail && showSupportDetail.id === requestId) {
        setShowSupportDetail(prev => ({ ...prev, status: newStatus, updatedAt: new Date() }));
      }
      
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status. Please try again.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Fetch emails for the selected group
  useEffect(() => {
    if (activeTab === 'email') {
      const fetchEmails = async () => {
        let emails = [];
        if (emailForm.group === 'waitlist' || emailForm.group === 'all') {
          emails = [...emails, ...waitlist.map(e => e.email).filter(Boolean)];
        }
        if (emailForm.group === 'newsletter' || emailForm.group === 'all') {
          emails = [...emails, ...newsletter.map(e => e.email).filter(Boolean)];
        }
        if (emailForm.group === 'app_users' || emailForm.group === 'all') {
          // Get emails from app users (users collection)
          emails = [...emails, ...users.map(u => u.email).filter(Boolean)];
        }
        // Remove duplicates
        emails = Array.from(new Set(emails));
        setEmailList(emails);
        setSelectedEmails(emails); // Default: all selected
        setSelectAll(true);
      };
      fetchEmails();
    }
  }, [activeTab, emailForm.group, waitlist, newsletter, users]);

  // Email Users tab logic
  const handleEmailFormChange = (e) => {
    const { name, value } = e.target;
    setEmailForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEmailCheckbox = (email) => {
    setSelectedEmails((prev) =>
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    );
    setSelectAll(false);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedEmails([]);
      setSelectAll(false);
    } else {
      setSelectedEmails(emailList);
      setSelectAll(true);
    }
  };

  const handleSendEmails = async (e) => {
    e.preventDefault();
    setSending(true);
    setSendResult(null);
    try {
      const res = await fetch(API_ENDPOINTS.sendBulkEmail, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: emailForm.subject,
          message: emailForm.message,
          recipients: selectedEmails,
          sender: auth.currentUser ? auth.currentUser.email : null
        })
      });
      const data = await res.json();
      setSending(false);
      setSendResult(data.success
        ? { success: true, message: `Emails sent to ${data.sent} recipient(s).${data.failed ? ` ${data.failed} failed.` : ''}` }
        : { success: false, message: data.message || 'Failed to send emails.' });
      // Refresh sent emails list
      if (activeTab === 'sent') {
        const db = getFirestore();
        const sentQuery = query(collection(db, 'sentEmails'), orderBy('timestamp', 'desc'));
        const snap = await getDocs(sentQuery);
        setSentEmails(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }
    } catch (err) {
      setSending(false);
      setSendResult({ success: false, message: err.message });
    }
  };

  // Filtered email list based on search
  const filteredEmailList = emailList.filter(email =>
    email.toLowerCase().includes(emailSearch.toLowerCase())
  );

  // Helper: get initials from email
  const getInitials = (email) => {
    const name = email.split('@')[0];
    return name.slice(0, 2).toUpperCase();
  };

  // Helper: get initials from name
  const getNameInitials = (firstName, lastName) => {
    const first = firstName ? firstName.charAt(0) : '';
    const last = lastName ? lastName.charAt(0) : '';
    return (first + last).toUpperCase();
  };

  // Helper: format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  // Filter support requests
  const filteredSupportRequests = supportRequests.filter(request => {
    const matchesSearch = !supportSearch || 
      request.fullName?.toLowerCase().includes(supportSearch.toLowerCase()) ||
      request.userEmail?.toLowerCase().includes(supportSearch.toLowerCase()) ||
      request.subject?.toLowerCase().includes(supportSearch.toLowerCase()) ||
      request.message?.toLowerCase().includes(supportSearch.toLowerCase());
    
    const matchesStatus = supportStatusFilter === 'all' || request.status === supportStatusFilter;
    const isResolved = request.status === 'resolved';
    
    return matchesSearch && matchesStatus && (showResolved ? true : !isResolved);
  });

  // Enhanced filter and sort users
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users.filter(user => {
      // Search filter
      const matchesSearch = !userSearch || 
        user.firstName?.toLowerCase().includes(userSearch.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(userSearch.toLowerCase()) ||
        user.email?.toLowerCase().includes(userSearch.toLowerCase()) ||
        user.fullName?.toLowerCase().includes(userSearch.toLowerCase());
      
      // Premium status filter
      const matchesPremium = userPremiumFilter === 'all' ||
        (userPremiumFilter === 'premium' && user.premium) ||
        (userPremiumFilter === 'free' && !user.premium);
      
      // Date range filter
      let matchesDate = true;
      if (userDateFilter.start || userDateFilter.end) {
        const userDate = user.createdAt?.toDate ? user.createdAt.toDate() : null;
        if (userDate) {
          if (userDateFilter.start) {
            const startDate = new Date(userDateFilter.start);
            startDate.setHours(0, 0, 0, 0);
            if (userDate < startDate) matchesDate = false;
          }
          if (userDateFilter.end) {
            const endDate = new Date(userDateFilter.end);
            endDate.setHours(23, 59, 59, 999);
            if (userDate > endDate) matchesDate = false;
          }
        } else {
          matchesDate = false;
        }
      }
      
      return matchesSearch && matchesPremium && matchesDate;
    });
    
    // Sort users
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (userSortBy) {
        case 'name':
          aValue = (a.fullName || `${a.firstName || ''} ${a.lastName || ''}`).toLowerCase();
          bValue = (b.fullName || `${b.firstName || ''} ${b.lastName || ''}`).toLowerCase();
          break;
        case 'email':
          aValue = (a.email || '').toLowerCase();
          bValue = (b.email || '').toLowerCase();
          break;
        case 'createdAt':
          aValue = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
          bValue = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
          break;
        case 'premium':
          aValue = a.premium ? 1 : 0;
          bValue = b.premium ? 1 : 0;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return userSortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return userSortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  }, [users, userSearch, userPremiumFilter, userDateFilter, userSortBy, userSortOrder]);

  // Legacy filteredUsers for backward compatibility
  const filteredUsers = filteredAndSortedUsers;

  const renderWaitlist = () => (
    <div className="data-table-container">
      {waitlist.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <div className="empty-state-text">No waitlist submissions</div>
          <div className="empty-state-subtext">Waitlist entries will appear here</div>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Features</th>
              <th>Vision</th>
              <th>Submitted</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
      {waitlist.map((entry) => (
              <tr key={entry.id}>
          {editId === entry.id ? (
            <>
                    <td>
                      <input 
                        name="name" 
                        value={editData.name || ''} 
                        onChange={handleEditChange}
                        className="table-input"
                      />
                    </td>
                    <td>
                      <input 
                        name="email" 
                        value={editData.email || ''} 
                        onChange={handleEditChange}
                        className="table-input"
                      />
                    </td>
                    <td>
                      <input 
                        name="features" 
                        value={Array.isArray(editData.features) ? editData.features.join(', ') : ''} 
                        onChange={e => setEditData({ ...editData, features: e.target.value.split(',').map(f => f.trim()) })}
                        className="table-input"
                      />
                    </td>
                    <td>
                      <textarea 
                        name="vision" 
                        value={editData.vision || ''} 
                        onChange={handleEditChange}
                        className="table-textarea"
                        rows={2}
                      />
                    </td>
                    <td>
                      {entry.timestamp?.toDate ? new Date(entry.timestamp.toDate()).toLocaleString() : ''}
                    </td>
                    <td>
                      <div className="table-actions">
                        <button onClick={() => handleEditSave('waitlist')} className="table-btn table-btn-primary">Save</button>
                        <button onClick={() => setEditId(null)} className="table-btn table-btn-secondary">Cancel</button>
                      </div>
                    </td>
            </>
          ) : (
            <>
                    <td><strong>{entry.name}</strong></td>
                    <td>{entry.email}</td>
                    <td>{Array.isArray(entry.features) ? entry.features.join(', ') : ''}</td>
                    <td className="table-cell-truncate" title={entry.vision}>{entry.vision || 'N/A'}</td>
                    <td>{entry.timestamp?.toDate ? new Date(entry.timestamp.toDate()).toLocaleString() : ''}</td>
                    <td>
                      <div className="table-actions">
                        <button onClick={() => handleEdit(entry)} className="table-btn table-btn-primary">Edit</button>
                        <button onClick={() => handleDelete('waitlist', entry.id)} className="table-btn table-btn-danger">Delete</button>
                      </div>
                    </td>
            </>
          )}
              </tr>
      ))}
          </tbody>
        </table>
      )}
    </div>
  );

  const renderNewsletter = () => (
    <div className="data-table-container">
      {newsletter.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📧</div>
          <div className="empty-state-text">No newsletter subscriptions</div>
          <div className="empty-state-subtext">Newsletter entries will appear here</div>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Submitted</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
      {newsletter.map((entry) => (
              <tr key={entry.id}>
          {editId === entry.id ? (
            <>
                    <td>
                      <input 
                        name="email" 
                        value={editData.email || ''} 
                        onChange={handleEditChange}
                        className="table-input"
                      />
                    </td>
                    <td>
                      {entry.timestamp?.toDate ? new Date(entry.timestamp.toDate()).toLocaleString() : ''}
                    </td>
                    <td>
                      <div className="table-actions">
                        <button onClick={() => handleEditSave('newsletter')} className="table-btn table-btn-primary">Save</button>
                        <button onClick={() => setEditId(null)} className="table-btn table-btn-secondary">Cancel</button>
                      </div>
                    </td>
            </>
          ) : (
            <>
                    <td><strong>{entry.email}</strong></td>
                    <td>{entry.timestamp?.toDate ? new Date(entry.timestamp.toDate()).toLocaleString() : ''}</td>
                    <td>
                      <div className="table-actions">
                        <button onClick={() => handleEdit(entry)} className="table-btn table-btn-primary">Edit</button>
                        <button onClick={() => handleDelete('newsletter', entry.id)} className="table-btn table-btn-danger">Delete</button>
                      </div>
                    </td>
            </>
          )}
              </tr>
      ))}
          </tbody>
        </table>
      )}
    </div>
  );

  const renderEmailUsers = () => (
    <div className="email-users-form-container improved-ui">
      <form className="email-users-form card" onSubmit={handleSendEmails}>
        <h2 className="form-title">Send Email to Users</h2>
        <div style={{marginBottom: '1rem', padding: '0.875rem', background: 'var(--surface-variant)', borderRadius: '12px', fontSize: '0.9rem', color: 'var(--on-surface-variant)'}}>
          <strong>Available Groups:</strong> Waitlist ({waitlist.length}), Newsletter ({newsletter.length}), App Users ({users.length}), All ({Array.from(new Set([...waitlist.map(e => e.email), ...newsletter.map(e => e.email), ...users.map(u => u.email)].filter(Boolean))).length})
        </div>
        <div className="form-section">
          <label className="form-label">
            Recipient Group
            <Select
              classNamePrefix="react-select"
              options={emailTypeOptions}
              value={emailTypeOptions.find(opt => opt.value === emailForm.group)}
              onChange={opt => setEmailForm(prev => ({ ...prev, group: opt.value }))}
              isSearchable={false}
              styles={{
                control: (base, state) => ({
                  ...base,
                  background: 'var(--surface-variant)',
                  borderColor: state.isFocused ? 'var(--primary-color)' : 'var(--outline-color)',
                  boxShadow: state.isFocused ? '0 0 0 3px rgba(139, 92, 246, 0.1)' : 'none',
                  borderRadius: 16,
                  minHeight: 44,
                  fontSize: '1rem',
                  color: 'var(--on-background)',
                }),
                option: (base, state) => ({
                  ...base,
                  background: state.isSelected ? 'rgba(139, 92, 246, 0.1)' : state.isFocused ? 'rgba(139, 92, 246, 0.05)' : 'var(--surface-variant)',
                  color: state.isSelected ? 'var(--primary-color)' : 'var(--on-background)',
                  fontWeight: state.isSelected ? 700 : 500,
                  fontSize: '1rem',
                  cursor: 'pointer',
                }),
                singleValue: base => ({ ...base, color: 'var(--on-background)', fontWeight: 600 }),
                dropdownIndicator: base => ({ ...base, color: 'var(--primary-color)' }),
                indicatorSeparator: base => ({ ...base, display: 'none' }),
                menu: base => ({ ...base, borderRadius: 16, boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)', border: '1px solid var(--outline-color)' }),
              }}
            />
          </label>
        </div>
        <div className="form-section">
          <label className="form-label">Search Recipients</label>
          <input
            type="text"
            className="form-input"
            placeholder="Search emails..."
            value={emailSearch}
            onChange={e => setEmailSearch(e.target.value)}
            style={{marginBottom: '0.7rem'}}
          />
          <label className="select-all-label">
            <input type="checkbox" checked={selectAll} onChange={handleSelectAll} /> Select All
          </label>
          <div className="email-chips-scrollbox">
            <div className="email-chips-grid">
              {filteredEmailList.length === 0 ? (
                <div className="empty-list">No emails found.</div>
              ) : (
                filteredEmailList.map(email => (
                  <div
                    key={email}
                    className={`email-chip${selectedEmails.includes(email) ? ' selected' : ''}`}
                    onClick={() => handleEmailCheckbox(email)}
                    title={email}
                  >
                    <span className="chip-avatar">{getInitials(email)}</span>
                    <span className="chip-email">{email}</span>
                    <input
                      type="checkbox"
                      checked={selectedEmails.includes(email)}
                      onChange={() => handleEmailCheckbox(email)}
                      style={{display: 'none'}}
                    />
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        <div className="form-section">
          <label className="form-label">Subject
            <input
              type="text"
              name="subject"
              value={emailForm.subject}
              onChange={handleEmailFormChange}
              required
              className="form-input"
              placeholder="Email subject"
            />
          </label>
        </div>
        <div className="form-section">
          <label className="form-label">Message
            <textarea
              name="message"
              value={emailForm.message}
              onChange={handleEmailFormChange}
              required
              className="form-textarea"
              placeholder="Type your message here..."
              rows={7}
            />
          </label>
        </div>
        <button type="submit" disabled={sending || selectedEmails.length === 0} className="send-btn">
          {sending ? 'Sending...' : `Send Email${selectedEmails.length > 1 ? 's' : ''}`}
        </button>
        {sendResult && (
          <div className={`email-send-result ${sendResult.success ? 'success' : 'error'}`}>{sendResult.message}</div>
        )}
      </form>
      <div className="email-preview card">
        <h3 className="preview-title">Preview</h3>
        <div style={{fontFamily: 'Inter, sans-serif', background: 'var(--background-color)', color: 'var(--on-background)', padding: 24, borderRadius: 20}}>
          <div style={{textAlign: 'center', marginBottom: 24}}>
            <img src='https://vivy.netlify.app/images/Logo.png' alt="Vivy Logo" style={{height: 60, marginBottom: 12}} />
            <h2 style={{color: 'var(--on-background)', fontSize: 22, marginBottom: 12, fontWeight: 700}}>{emailForm.subject || 'Email Subject'}</h2>
          </div>
          <div style={{background: 'var(--surface-variant)', padding: 20, borderRadius: 16, boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)', border: '1px solid var(--outline-color)'}}>
            <div style={{fontSize: 15, lineHeight: 1.7, marginBottom: 18, whiteSpace: 'pre-line', color: 'var(--on-background)'}}>{emailForm.message || 'Email body will appear here.'}</div>
          </div>
          <div style={{textAlign: 'center', marginTop: 18, color: 'var(--on-surface-variant)'}}>
            <p style={{marginBottom: 5}}>Best regards,</p>
            <p style={{fontWeight: 600, color: 'var(--on-background)'}}>The Vivy Team</p>
            <p style={{fontWeight: 600, color: 'var(--on-background)'}}>
              © 2025 Vivy. Handcrafted by <a href="https://hady.netlify.app" target="_blank" rel="noopener noreferrer" style={{color: 'var(--primary-color)'}}>Hady Korshem</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Fetch sent emails when Sent Emails tab is active
  useEffect(() => {
    if (activeTab === 'sent') {
      const fetchSentEmails = async () => {
        const db = getFirestore();
        const sentQuery = query(collection(db, 'sentEmails'), orderBy('timestamp', 'desc'));
        const snap = await getDocs(sentQuery);
        setSentEmails(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      };
      fetchSentEmails();
    }
  }, [activeTab]);

  // Support Requests tab
  const renderSupportRequests = () => (
    <div className="support-requests-container">
      <div className="search-filter-bar" style={{gap: '1.2rem', alignItems: 'center', marginBottom: 16}}>
        <span style={{fontWeight: 600, color: 'var(--primary-color)', fontSize: 18, display: 'flex', alignItems: 'center', gap: 8}}>
          <span role="img" aria-label="filter">🔍</span> Filter:
        </span>
        <input
          type="text"
          className="search-input"
          placeholder="Search support requests..."
          value={supportSearch}
          onChange={(e) => setSupportSearch(e.target.value)}
          style={{minWidth: 220, maxWidth: 320}}
        />
        <select
          className="status-filter"
          value={supportStatusFilter}
          onChange={(e) => setSupportStatusFilter(e.target.value)}
          style={{minWidth: 140}}
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="in-progress">In Progress</option>
          <option value="resolved">Resolved</option>
        </select>
        <button
          onClick={() => setShowResolved(r => !r)}
          style={{
            background: showResolved ? 'linear-gradient(90deg, var(--primary-color) 0%, var(--secondary-color) 100%)' : 'var(--surface-variant)',
            color: showResolved ? '#fff' : 'var(--primary-color)',
            border: showResolved ? 'none' : '1px solid var(--outline-color)',
            borderRadius: 16,
            padding: '0.875rem 1.5rem',
            fontWeight: 700,
            fontSize: '1rem',
            cursor: 'pointer',
            boxShadow: showResolved ? '0 4px 16px rgba(139, 92, 246, 0.2)' : 'none',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          <span role="img" aria-label="resolved"></span>
          {showResolved ? 'Hide Resolved' : 'Show Resolved'}
        </button>
      </div>

      {filteredSupportRequests.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📧</div>
          <div className="empty-state-text">No support requests found</div>
          <div className="empty-state-subtext">
            {supportSearch || supportStatusFilter !== 'all' 
              ? 'Try adjusting your search or filters' 
              : 'Support requests from users will appear here'}
          </div>
        </div>
      ) : (
        <div className="data-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Subject</th>
                <th>Category</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
          {filteredSupportRequests.map(request => (
                <tr key={request.id}>
                  <td>
                    <div className="table-user-info">
                      <div className="table-avatar">
                  {getNameInitials(request.firstName, request.lastName)}
                </div>
                      <span><strong>{request.fullName || 'Unknown User'}</strong></span>
                    </div>
                  </td>
                  <td>{request.userEmail}</td>
                  <td className="table-cell-truncate" title={request.subject}>{request.subject}</td>
                  <td>
                    <span className="request-category">{request.category}</span>
                  </td>
                  <td>
                    <span className={`request-status ${request.status}`}>{request.status}</span>
                  </td>
                  <td>{formatDate(request.createdAt)}</td>
                  <td>
                    <div className="table-actions">
                  <button 
                        className="table-btn table-btn-primary"
                        onClick={() => setShowSupportDetail(request)}
                      >
                        View
                      </button>
                      <button 
                        className="table-btn table-btn-secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      const nextStatus = request.status === 'pending' ? 'in-progress' : 
                                       request.status === 'in-progress' ? 'resolved' : 'pending';
                      updateSupportStatus(request.id, nextStatus);
                    }}
                    disabled={updatingStatus}
                  >
                        {updatingStatus ? '...' : 'Next'}
                  </button>
                </div>
                  </td>
                </tr>
          ))}
            </tbody>
          </table>
        </div>
      )}

      {showSupportDetail && (
        <div className="support-detail-modal" onClick={() => setShowSupportDetail(null)}>
          <div className="support-detail-content" onClick={(e) => e.stopPropagation()}>
            <div className="support-detail-header">
              <button className="close-btn" onClick={() => setShowSupportDetail(null)}>&times;</button>
              <div className="support-detail-title">{showSupportDetail.subject}</div>
              <div className="support-detail-meta">
                <div className="support-detail-info">
                  <span className="support-detail-label">From:</span>
                  <span>{showSupportDetail.fullName}</span>
                </div>
                <div className="support-detail-info">
                  <span className="support-detail-label">Email:</span>
                  <span>{showSupportDetail.userEmail}</span>
                </div>
                <div className="support-detail-info">
                  <span className="support-detail-label">Category:</span>
                  <span>{showSupportDetail.category}</span>
                </div>
                <div className="support-detail-info">
                  <span className="support-detail-label">Status:</span>
                  <span className={`request-status ${showSupportDetail.status}`}>{showSupportDetail.status}</span>
                </div>
                <div className="support-detail-info">
                  <span className="support-detail-label">Date:</span>
                  <span>{formatDate(showSupportDetail.createdAt)}</span>
                </div>
              </div>
            </div>
            <div className="support-detail-body">
              <div className="support-detail-message">{showSupportDetail.message}</div>
              
              {/* Status Update Section */}
              <div style={{marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid var(--outline-color)'}}>
                <h4 style={{marginBottom: '1rem', color: 'var(--on-background)', fontSize: '1.1rem', fontWeight: 600}}>Update Status</h4>
                <div style={{display: 'flex', gap: '1rem', flexWrap: 'wrap'}}>
                  {['pending', 'in-progress', 'resolved'].map(status => (
                    <button
                      key={status}
                      onClick={() => updateSupportStatus(showSupportDetail.id, status)}
                      disabled={updatingStatus || showSupportDetail.status === status}
                      style={{
                        padding: '0.6rem 1.2rem',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        cursor: updatingStatus || showSupportDetail.status === status ? 'not-allowed' : 'pointer',
                        opacity: updatingStatus || showSupportDetail.status === status ? 0.6 : 1,
                        background: showSupportDetail.status === status ? 'var(--primary-color)' : 'var(--surface-variant)',
                        color: showSupportDetail.status === status ? '#fff' : 'var(--primary-color)',
                        border: showSupportDetail.status === status ? 'none' : '1px solid var(--outline-color)',
                        transition: 'all 0.2s'
                      }}
                    >
                      {updatingStatus ? 'Updating...' : status.charAt(0).toUpperCase() + status.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Export users to CSV
  const exportUsersToCSV = () => {
    const headers = ['Name', 'Email', 'First Name', 'Last Name', 'Premium Status', 'Premium Type', 'Created At', 'Last Updated'];
    const rows = filteredAndSortedUsers.map(user => [
      user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown',
      user.email || '',
      user.firstName || '',
      user.lastName || '',
      user.premium ? 'Yes' : 'No',
      user.premium_type || 'None',
      user.createdAt?.toDate ? user.createdAt.toDate().toLocaleString() : 'N/A',
      formatDate(user.updatedAt)
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `vivy-users-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Users tab
  const renderUsers = () => {
    const premiumCount = users.filter(u => u.premium).length;
    const freeCount = users.length - premiumCount;
    
    return (
    <div className="users-container">
      {/* Statistics Cards */}
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '2rem'}}>
        <div className="card" style={{padding: '1.5rem', textAlign: 'center'}}>
          <div style={{fontSize: '2.5rem', fontWeight: 700, color: 'var(--primary-color)', marginBottom: '0.5rem'}}>{users.length}</div>
          <div style={{color: 'var(--on-surface-variant)', fontSize: '0.9rem'}}>Total Users</div>
        </div>
        <div className="card" style={{padding: '1.5rem', textAlign: 'center'}}>
          <div style={{fontSize: '2.5rem', fontWeight: 700, color: 'var(--success-color)', marginBottom: '0.5rem'}}>{premiumCount}</div>
          <div style={{color: 'var(--on-surface-variant)', fontSize: '0.9rem'}}>Premium Users</div>
        </div>
        <div className="card" style={{padding: '1.5rem', textAlign: 'center'}}>
          <div style={{fontSize: '2.5rem', fontWeight: 700, color: 'var(--on-surface-variant)', marginBottom: '0.5rem'}}>{freeCount}</div>
          <div style={{color: 'var(--on-surface-variant)', fontSize: '0.9rem'}}>Free Users</div>
        </div>
        <div className="card" style={{padding: '1.5rem', textAlign: 'center'}}>
          <div style={{fontSize: '2.5rem', fontWeight: 700, color: 'var(--primary-color)', marginBottom: '0.5rem'}}>{filteredAndSortedUsers.length}</div>
          <div style={{color: 'var(--on-surface-variant)', fontSize: '0.9rem'}}>Filtered Results</div>
        </div>
      </div>

      <div className="search-filter-bar" style={{flexWrap: 'wrap', gap: '1rem'}}>
        <input
          type="text"
          className="search-input"
          placeholder="Search users by name or email..."
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          style={{flex: '1 1 300px', minWidth: '250px'}}
        />
        <select
          className="status-filter"
          value={userPremiumFilter}
          onChange={(e) => setUserPremiumFilter(e.target.value)}
          style={{minWidth: '140px'}}
        >
          <option value="all">All Users</option>
          <option value="premium">Premium Only</option>
          <option value="free">Free Only</option>
        </select>
        <button
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          style={{
            padding: '0.875rem 1.25rem',
            border: '1px solid var(--outline-color)',
            borderRadius: '16px',
            background: showAdvancedFilters ? 'var(--primary-color)' : 'var(--surface-variant)',
            color: showAdvancedFilters ? '#fff' : 'var(--on-background)',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: 600,
            transition: 'all 0.2s'
          }}
        >
          {showAdvancedFilters ? '▼' : '▶'} Advanced Filters
        </button>
        <button
          onClick={exportUsersToCSV}
          style={{
            padding: '0.875rem 1.25rem',
            border: '1px solid var(--primary-color)',
            borderRadius: '16px',
            background: 'var(--primary-color)',
            color: '#fff',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: 600,
            transition: 'all 0.2s'
          }}
        >
          📥 Export CSV
        </button>
      </div>

      {showAdvancedFilters && (
        <div className="card" style={{marginTop: '1rem', padding: '1.5rem'}}>
          <h3 style={{marginBottom: '1rem', fontSize: '1.1rem', fontWeight: 600, color: 'var(--on-background)'}}>Advanced Filters</h3>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem'}}>
            <div>
              <label className="form-label">Date Range - Start</label>
              <input
                type="date"
                className="form-input"
                value={userDateFilter.start}
                onChange={(e) => setUserDateFilter({...userDateFilter, start: e.target.value})}
              />
            </div>
            <div>
              <label className="form-label">Date Range - End</label>
              <input
                type="date"
                className="form-input"
                value={userDateFilter.end}
                onChange={(e) => setUserDateFilter({...userDateFilter, end: e.target.value})}
              />
            </div>
            <div>
              <label className="form-label">Sort By</label>
              <select
                className="form-select"
                value={userSortBy}
                onChange={(e) => setUserSortBy(e.target.value)}
              >
                <option value="createdAt">Created Date</option>
                <option value="name">Name</option>
                <option value="email">Email</option>
                <option value="premium">Premium Status</option>
              </select>
            </div>
            <div>
              <label className="form-label">Sort Order</label>
              <select
                className="form-select"
                value={userSortOrder}
                onChange={(e) => setUserSortOrder(e.target.value)}
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </div>
          </div>
          <button
            onClick={() => {
              setUserDateFilter({ start: '', end: '' });
              setUserPremiumFilter('all');
              setUserSortBy('createdAt');
              setUserSortOrder('desc');
            }}
            style={{
              padding: '0.6rem 1.2rem',
              border: '1px solid var(--outline-color)',
              borderRadius: '12px',
              background: 'var(--surface-variant)',
              color: 'var(--on-background)',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 600
            }}
          >
            Reset Filters
          </button>
        </div>
      )}

      {filteredUsers.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">👥</div>
          <div className="empty-state-text">No users found</div>
          <div className="empty-state-subtext">
            {userSearch 
              ? 'Try adjusting your search terms' 
              : `Found ${users.length} total users in database. Click Debug Info to see details.`}
          </div>
        </div>
      ) : (
        <div className="data-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Premium Status</th>
                <th>Premium Type</th>
                <th>Created At</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
          {filteredAndSortedUsers.map(user => {
                const isExpired = user.premium_expiry && user.premium_expiry.toDate && user.premium_expiry.toDate() < new Date();
                return (
                <tr key={user.id}>
                  <td>
                    <div className="table-user-info">
                      <div className="table-avatar">
                  {getNameInitials(user.firstName, user.lastName)}
                </div>
                      <span><strong>{user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown User'}</strong></span>
                </div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`request-status ${user.premium ? (isExpired ? 'pending' : 'resolved') : 'pending'}`}>
                      {user.premium ? (isExpired ? 'Expired' : 'Active') : 'Free'}
                    </span>
                  </td>
                  <td>{user.premium_type || 'None'}</td>
                  <td>
                    {user.createdAt
                      ? (user.createdAt.toDate
                          ? user.createdAt.toDate().toLocaleString()
                          : String(user.createdAt))
                      : 'N/A'}
                  </td>
                  <td>{formatDate(user.updatedAt)}</td>
                  <td>
                    <div className="table-actions">
                      <button 
                        onClick={() => setSelectedUser(user)} 
                        className="table-btn table-btn-primary"
                      >
                        View Details
                      </button>
                      {user.premium && (
                        <button 
                          onClick={() => {
                            setSearchedUser(user);
                          }}
                          className="table-btn table-btn-secondary"
                          style={{marginLeft: '0.5rem'}}
                        >
                          Manage Subscription
                        </button>
                      )}
              </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
                </div>
      )}
    </div>
    );
  };

  const updateUserPremium = async (premium, type, days) => {
    if (!searchedUser) return;
    setUpdatingPremium(true);
    try {
      const updates = {
        premium: premium,
        premium_type: premium ? type : null,
        premium_source: premium ? 'admin_override' : null,
        updated_at: Timestamp.now()
      };
      
      if (premium && type !== 'lifetime' && type !== 'whitelist') {
        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + (days || 30));
        updates.premium_expiry = Timestamp.fromDate(expiryDate);
      } else if (premium && (type === 'lifetime' || type === 'whitelist')) {
        updates.premium_expiry = null;
      } else {
        updates.premium_expiry = null;
      }
      
      await updateDoc(doc(db, 'users', searchedUser.id), updates);
      const updatedUser = { ...searchedUser, ...updates };
      setSearchedUser(updatedUser);
      // Update users array
      setUsers(prevUsers => prevUsers.map(u => u.id === searchedUser.id ? updatedUser : u));
      alert('Premium status updated successfully');
      fetchData();
    } catch (error) {
      console.error('Error updating premium:', error);
      alert('Error updating premium: ' + error.message);
    }
    setUpdatingPremium(false);
  };

  const createPromoCode = async () => {
    if (!newPromoCode.code.trim()) {
      alert('Please enter a promo code');
      return;
    }
    setCreatingPromo(true);
    try {
      const promoData = {
        discount_type: newPromoCode.discountType,
        value: newPromoCode.discountType === 'lifetime' ? 1 : parseInt(newPromoCode.value) || 30,
        expires_at: newPromoCode.expiresAt ? Timestamp.fromDate(new Date(newPromoCode.expiresAt)) : null,
        max_uses: parseInt(newPromoCode.maxUses) || 0,
        used_by: [],
        created_at: Timestamp.now()
      };
      
      await setDoc(doc(db, 'promo_codes', newPromoCode.code.toUpperCase().trim()), promoData);
      setPromoCodes([...promoCodes, { id: newPromoCode.code.toUpperCase().trim(), ...promoData }]);
      setNewPromoCode({ code: '', discountType: 'free', value: 30, expiresAt: '', maxUses: 0 });
      alert('Promo code created successfully');
      fetchData();
    } catch (error) {
      console.error('Error creating promo code:', error);
      alert('Error creating promo code: ' + error.message);
    }
    setCreatingPromo(false);
  };

  const deletePromoCode = async (codeId) => {
    if (!window.confirm('Are you sure you want to delete this promo code?')) return;
    try {
      await deleteDoc(doc(db, 'promo_codes', codeId));
      setPromoCodes(promoCodes.filter(p => p.id !== codeId));
      alert('Promo code deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting promo code:', error);
      alert('Error deleting promo code: ' + error.message);
    }
  };

  // Filtered and sorted subscriptions - moved outside render function
  const filteredAndSortedUsersForPremium = useMemo(() => {
      let filtered = users.filter(user => {
        const matchesSearch = !subscriptionSearch || 
          user.email?.toLowerCase().includes(subscriptionSearch.toLowerCase()) ||
          user.fullName?.toLowerCase().includes(subscriptionSearch.toLowerCase());
        
        const matchesPremium = premiumFilter === 'all' || 
          (premiumFilter === 'active' && user.premium) ||
          (premiumFilter === 'inactive' && !user.premium);
        
        // Subscription type filter
        const matchesType = subscriptionTypeFilter === 'all' ||
          (subscriptionTypeFilter === 'lifetime' && user.premium_type === 'lifetime') ||
          (subscriptionTypeFilter === 'monthly' && user.premium_type === 'monthly') ||
          (subscriptionTypeFilter === 'yearly' && user.premium_type === 'yearly') ||
          (subscriptionTypeFilter === 'free' && user.premium_type === 'free') ||
          (subscriptionTypeFilter === 'whitelist' && user.premium_type === 'whitelist') ||
          (subscriptionTypeFilter === 'none' && !user.premium_type);
        
        // Expiry filter
        let matchesExpiry = true;
        if (subscriptionExpiryFilter !== 'all' && user.premium_expiry) {
          const expiryDate = user.premium_expiry.toDate ? user.premium_expiry.toDate() : null;
          if (expiryDate) {
            const now = new Date();
            if (subscriptionExpiryFilter === 'expired' && expiryDate >= now) matchesExpiry = false;
            if (subscriptionExpiryFilter === 'expiring_soon' && (expiryDate < now || expiryDate > new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000))) matchesExpiry = false;
            if (subscriptionExpiryFilter === 'active' && expiryDate < now) matchesExpiry = false;
          }
        } else if (subscriptionExpiryFilter === 'expired' && user.premium && user.premium_expiry) {
          const expiryDate = user.premium_expiry.toDate ? user.premium_expiry.toDate() : null;
          if (expiryDate && expiryDate < new Date()) matchesExpiry = true;
          else matchesExpiry = false;
        } else if (subscriptionExpiryFilter === 'expiring_soon' && user.premium && user.premium_expiry) {
          const expiryDate = user.premium_expiry.toDate ? user.premium_expiry.toDate() : null;
          if (expiryDate) {
            const now = new Date();
            const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
            matchesExpiry = expiryDate >= now && expiryDate <= weekFromNow;
          } else matchesExpiry = false;
        }
        
        return matchesSearch && matchesPremium && matchesType && matchesExpiry;
      });
      
      // Sort subscriptions
      filtered.sort((a, b) => {
        let aValue, bValue;
        
        switch (subscriptionSortBy) {
          case 'email':
            aValue = (a.email || '').toLowerCase();
            bValue = (b.email || '').toLowerCase();
            break;
          case 'name':
            aValue = (a.fullName || `${a.firstName || ''} ${a.lastName || ''}`).toLowerCase();
            bValue = (b.fullName || `${b.firstName || ''} ${b.lastName || ''}`).toLowerCase();
            break;
          case 'premium_type':
            aValue = a.premium_type || '';
            bValue = b.premium_type || '';
            break;
          case 'expiry':
            aValue = a.premium_expiry?.toDate ? a.premium_expiry.toDate().getTime() : 0;
            bValue = b.premium_expiry?.toDate ? b.premium_expiry.toDate().getTime() : 0;
            break;
          default:
            return 0;
        }
        
        if (aValue < bValue) return subscriptionSortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return subscriptionSortOrder === 'asc' ? 1 : -1;
        return 0;
      });
      
      return filtered;
    }, [users, subscriptionSearch, premiumFilter, subscriptionTypeFilter, subscriptionExpiryFilter, subscriptionSortBy, subscriptionSortOrder]);
  
  const renderSubscriptions = () => {
    const filteredUsersForPremium = filteredAndSortedUsersForPremium;

    const premiumStats = {
      total: users.length,
      active: users.filter(u => u.premium && (!u.premium_expiry || u.premium_expiry.toDate() >= new Date())).length,
      expired: users.filter(u => u.premium && u.premium_expiry && u.premium_expiry.toDate() < new Date()).length,
      lifetime: users.filter(u => u.premium_type === 'lifetime').length,
      expiringSoon: users.filter(u => {
        if (!u.premium || !u.premium_expiry) return false;
        const expiry = u.premium_expiry.toDate();
        const now = new Date();
        const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        return expiry >= now && expiry <= weekFromNow;
      }).length
    };

    return (
      <div className="subscriptions-container" style={{display: 'flex', flexDirection: 'column', gap: '2rem'}}>
        {/* Statistics Cards */}
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1.5rem', marginBottom: '1rem'}}>
          <div className="card" style={{padding: '1.5rem', textAlign: 'center'}}>
            <div style={{fontSize: '2.5rem', fontWeight: 700, color: 'var(--primary-color)', marginBottom: '0.5rem'}}>{premiumStats.total}</div>
            <div style={{color: 'var(--on-surface-variant)', fontSize: '0.9rem'}}>Total Users</div>
          </div>
          <div className="card" style={{padding: '1.5rem', textAlign: 'center'}}>
            <div style={{fontSize: '2.5rem', fontWeight: 700, color: 'var(--success-color)', marginBottom: '0.5rem'}}>{premiumStats.active}</div>
            <div style={{color: 'var(--on-surface-variant)', fontSize: '0.9rem'}}>Active Premium</div>
          </div>
          <div className="card" style={{padding: '1.5rem', textAlign: 'center'}}>
            <div style={{fontSize: '2.5rem', fontWeight: 700, color: 'var(--warning-color)', marginBottom: '0.5rem'}}>{premiumStats.expired}</div>
            <div style={{color: 'var(--on-surface-variant)', fontSize: '0.9rem'}}>Expired</div>
          </div>
          <div className="card" style={{padding: '1.5rem', textAlign: 'center'}}>
            <div style={{fontSize: '2.5rem', fontWeight: 700, color: 'var(--primary-color)', marginBottom: '0.5rem'}}>{premiumStats.lifetime}</div>
            <div style={{color: 'var(--on-surface-variant)', fontSize: '0.9rem'}}>Lifetime</div>
          </div>
          <div className="card" style={{padding: '1.5rem', textAlign: 'center'}}>
            <div style={{fontSize: '2.5rem', fontWeight: 700, color: 'var(--tertiary-color)', marginBottom: '0.5rem'}}>{premiumStats.expiringSoon}</div>
            <div style={{color: 'var(--on-surface-variant)', fontSize: '0.9rem'}}>Expiring Soon</div>
          </div>
        </div>

        {/* Users Premium Management Table */}
        <div>
          <div className="search-filter-bar" style={{marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem'}}>
            <h2 className="form-title" style={{margin: 0, fontSize: '1.5rem', flex: '1 1 100%'}}>Premium Users Management</h2>
            <input
              type="text"
              className="search-input"
              placeholder="Search by email or name..."
              value={subscriptionSearch}
              onChange={(e) => setSubscriptionSearch(e.target.value)}
              style={{flex: '1 1 300px', minWidth: '250px', maxWidth: '400px'}}
            />
            <select
              className="status-filter"
              value={premiumFilter}
              onChange={(e) => setPremiumFilter(e.target.value)}
              style={{minWidth: '140px'}}
            >
              <option value="all">All Users</option>
              <option value="active">Premium Active</option>
              <option value="inactive">Premium Inactive</option>
            </select>
            <select
              className="status-filter"
              value={subscriptionTypeFilter}
              onChange={(e) => setSubscriptionTypeFilter(e.target.value)}
              style={{minWidth: '160px'}}
            >
              <option value="all">All Types</option>
              <option value="lifetime">Lifetime</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
              <option value="free">Free Trial</option>
              <option value="whitelist">Whitelist</option>
              <option value="none">No Subscription</option>
            </select>
            <select
              className="status-filter"
              value={subscriptionExpiryFilter}
              onChange={(e) => setSubscriptionExpiryFilter(e.target.value)}
              style={{minWidth: '160px'}}
            >
              <option value="all">All Expiry Status</option>
              <option value="active">Active</option>
              <option value="expiring_soon">Expiring Soon (7 days)</option>
              <option value="expired">Expired</option>
            </select>
            <select
              className="status-filter"
              value={subscriptionSortBy}
              onChange={(e) => setSubscriptionSortBy(e.target.value)}
              style={{minWidth: '140px'}}
            >
              <option value="email">Sort: Email</option>
              <option value="name">Sort: Name</option>
              <option value="premium_type">Sort: Type</option>
              <option value="expiry">Sort: Expiry</option>
            </select>
            <button
              onClick={() => setSubscriptionSortOrder(subscriptionSortOrder === 'asc' ? 'desc' : 'asc')}
              style={{
                padding: '0.875rem 1rem',
                border: '1px solid var(--outline-color)',
                borderRadius: '16px',
                background: 'var(--surface-variant)',
                color: 'var(--on-background)',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 600
              }}
              title={`Sort ${subscriptionSortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
            >
              {subscriptionSortOrder === 'asc' ? '↑' : '↓'}
            </button>
            <button
              onClick={() => {
                const headers = ['Name', 'Email', 'Premium Status', 'Premium Type', 'Premium Expiry', 'Premium Source'];
                const rows = filteredUsersForPremium.map(user => [
                  user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown',
                  user.email || '',
                  user.premium ? 'Active' : 'Inactive',
                  user.premium_type || 'None',
                  user.premium_expiry?.toDate ? user.premium_expiry.toDate().toLocaleDateString() : (user.premium_type === 'lifetime' ? 'Never' : 'N/A'),
                  user.premium_source || 'None'
                ]);
                const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `vivy-subscriptions-${new Date().toISOString().split('T')[0]}.csv`;
                link.click();
              }}
              style={{
                padding: '0.875rem 1.25rem',
                border: '1px solid var(--primary-color)',
                borderRadius: '16px',
                background: 'var(--primary-color)',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 600
              }}
            >
              📥 Export CSV
            </button>
          </div>

          {filteredUsersForPremium.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">👥</div>
              <div className="empty-state-text">No users found</div>
              <div className="empty-state-subtext">
                {subscriptionSearch || premiumFilter !== 'all' 
                  ? 'Try adjusting your search or filters' 
                  : 'Users will appear here'}
              </div>
            </div>
          ) : (
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Premium Status</th>
                    <th>Premium Type</th>
                    <th>Premium Expiry</th>
                    <th>Premium Source</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsersForPremium.map(user => {
                    const isExpired = user.premium_expiry && user.premium_expiry.toDate && user.premium_expiry.toDate() < new Date();
                    return (
                      <tr key={user.id}>
                        <td>
                          <div className="table-user-info">
                            <div className="table-avatar">
                              {getNameInitials(user.firstName, user.lastName)}
                            </div>
                            <span><strong>{user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown User'}</strong></span>
                          </div>
                        </td>
                        <td>{user.email}</td>
                        <td>
                          <span className={`request-status ${user.premium ? (isExpired ? 'pending' : 'resolved') : 'pending'}`}>
                            {user.premium ? (isExpired ? 'Expired' : 'Active') : 'Inactive'}
                          </span>
                        </td>
                        <td>{user.premium_type || 'None'}</td>
                        <td>
                          {user.premium_expiry 
                            ? (user.premium_expiry.toDate ? user.premium_expiry.toDate().toLocaleDateString() : 'N/A')
                            : user.premium_type === 'lifetime' || user.premium_type === 'whitelist' ? 'Never' : 'N/A'}
                        </td>
                        <td>{user.premium_source || 'None'}</td>
                        <td>
                          <div className="table-actions">
                            <button
                              onClick={() => {
                                setSearchedUser(user);
                              }}
                              className="table-btn table-btn-primary"
                            >
                              Manage Subscription
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>


        {/* Promo Codes Section */}
        <div>
          <div className="search-filter-bar" style={{marginBottom: '1.5rem'}}>
            <h2 className="form-title" style={{margin: 0, fontSize: '1.5rem'}}>Promo Codes Management</h2>
          </div>
          
          {/* Create Promo Code */}
          <div className="card" style={{marginBottom: '2rem'}}>
            <h3 style={{marginBottom: '1.5rem', color: 'var(--on-background)', fontSize: '1.2rem', fontWeight: 700}}>Create New Promo Code</h3>
          <div className="form-section">
            <label className="form-label">Promo Code</label>
            <input
              type="text"
              className="form-input"
              placeholder="PROMO123"
              value={newPromoCode.code}
              onChange={(e) => setNewPromoCode({...newPromoCode, code: e.target.value.toUpperCase()})}
            />
          </div>
          <div className="form-section">
            <label className="form-label">Discount Type</label>
            <select
              className="form-select"
              value={newPromoCode.discountType}
              onChange={(e) => setNewPromoCode({...newPromoCode, discountType: e.target.value})}
            >
              <option value="free">Free Premium (days)</option>
              <option value="duration_days">Duration (days)</option>
              <option value="lifetime">Lifetime</option>
            </select>
          </div>
          {newPromoCode.discountType !== 'lifetime' && (
            <div className="form-section">
              <label className="form-label">Value (days)</label>
              <input
                type="number"
                className="form-input"
                value={newPromoCode.value}
                onChange={(e) => setNewPromoCode({...newPromoCode, value: parseInt(e.target.value) || 30})}
                min="1"
              />
            </div>
          )}
          <div className="form-section">
            <label className="form-label">Expires At (optional)</label>
            <input
              type="date"
              className="form-input"
              value={newPromoCode.expiresAt}
              onChange={(e) => setNewPromoCode({...newPromoCode, expiresAt: e.target.value})}
            />
          </div>
          <div className="form-section">
            <label className="form-label">Max Uses (0 = unlimited)</label>
            <input
              type="number"
              className="form-input"
              value={newPromoCode.maxUses}
              onChange={(e) => setNewPromoCode({...newPromoCode, maxUses: parseInt(e.target.value) || 0})}
              min="0"
            />
          </div>
          <button
            className="send-btn"
            onClick={createPromoCode}
            disabled={creatingPromo || !newPromoCode.code.trim()}
          >
            {creatingPromo ? 'Creating...' : 'Create Promo Code'}
          </button>
        </div>

          {/* Promo Codes List */}
          {promoCodes.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🎟️</div>
              <div className="empty-state-text">No promo codes found</div>
              <div className="empty-state-subtext">Create your first promo code above</div>
            </div>
          ) : (
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Type</th>
                    <th>Value</th>
                    <th>Uses</th>
                    <th>Expires</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {promoCodes.map(promo => {
                    const isExpired = promo.expires_at && promo.expires_at.toDate && promo.expires_at.toDate() < new Date();
                    const isMaxedOut = promo.max_uses > 0 && (promo.used_by?.length || 0) >= promo.max_uses;
                    return (
                      <tr key={promo.id}>
                        <td>
                          <div className="table-user-info">
                            <div className="table-avatar" style={{background: 'var(--success-color)'}}>
                              {promo.id.slice(0, 2)}
                            </div>
                            <span><strong>{promo.id}</strong></span>
                          </div>
                        </td>
                        <td>
                          <span className="request-category">{promo.discount_type}</span>
                        </td>
                        <td>
                          <strong>{promo.value}</strong>
                          {promo.discount_type !== 'lifetime' && <span style={{color: 'var(--on-surface-variant)', fontSize: '0.85rem'}}> days</span>}
                        </td>
                        <td>
                          <span className={`request-status ${isMaxedOut ? 'pending' : 'resolved'}`}>
                            {promo.used_by?.length || 0} / {promo.max_uses || '∞'}
                          </span>
                        </td>
                        <td>
                          {promo.expires_at 
                            ? (
                              <span style={{color: isExpired ? 'var(--error-color)' : 'var(--on-background)'}}>
                                {promo.expires_at.toDate ? promo.expires_at.toDate().toLocaleDateString() : 'N/A'}
                                {isExpired && ' (Expired)'}
                              </span>
                            )
                            : <span style={{color: 'var(--on-surface-variant)'}}>Never</span>}
                        </td>
                        <td>
                          {promo.created_at?.toDate 
                            ? promo.created_at.toDate().toLocaleDateString()
                            : 'N/A'}
                        </td>
                        <td>
                          <div className="table-actions">
                            <button
                              onClick={() => deletePromoCode(promo.id)}
                              className="table-btn table-btn-danger"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Improved Sent Emails tab
  const renderSentEmails = () => (
    <div>
      <h2 className="form-title" style={{marginBottom: '1.5rem'}}>Sent Emails</h2>
      {sentEmails.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📧</div>
          <div className="empty-state-text">No sent emails yet</div>
          <div className="empty-state-subtext">Sent emails will appear here</div>
        </div>
      ) : (
        <div className="data-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Subject</th>
                <th>Date</th>
                <th>Recipients</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sentEmails.map(email => (
                <tr key={email.id} style={{cursor: 'pointer'}}>
                  <td><strong>{email.subject}</strong></td>
                  <td>{email.timestamp?.toDate ? new Date(email.timestamp.toDate()).toLocaleString() : ''}</td>
                  <td>{email.recipients?.length || 0}</td>
                  <td>
                    <span className={`request-status ${email.failedCount > 0 ? 'pending' : 'resolved'}`}>
                      {email.failedCount > 0 ? `Partial (${email.failedCount} failed)` : 'Sent'}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button 
                        onClick={() => setShowSentDetail(email)} 
                        className="table-btn table-btn-primary"
                      >
                        View Details
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showSentDetail && (
        <div className="sent-detail-modal">
          <div className="sent-detail-content card">
            <button className="close-btn" onClick={() => setShowSentDetail(null)}>&times;</button>
            <h3 style={{marginBottom: 8}}>{showSentDetail.subject}</h3>
            <div style={{marginBottom: 8, color: '#888'}}>{showSentDetail.timestamp?.toDate ? new Date(showSentDetail.timestamp.toDate()).toLocaleString() : ''}</div>
            <div style={{marginBottom: 8}}><strong>Recipients:</strong> {showSentDetail.recipients?.join(', ')}</div>
            <div style={{marginBottom: 8}}><strong>Message:</strong><br/><div style={{whiteSpace: 'pre-line', marginTop: 4}}>{showSentDetail.message}</div></div>
            {showSentDetail.failedCount > 0 && (
              <div style={{color: '#e74c3c', marginTop: 8}}><strong>Failed:</strong> {showSentDetail.errors?.map(e => e.to).join(', ')}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="dashboard-flex">
      <aside className="dashboard-sidebar">
        <h2>Dashboard</h2>
        <nav>
          <ul>
            <li className={activeTab === 'waitlist' ? 'active' : ''} onClick={() => setActiveTab('waitlist')}>Waitlist</li>
            <li className={activeTab === 'newsletter' ? 'active' : ''} onClick={() => setActiveTab('newsletter')}>Newsletter</li>
            <li className={activeTab === 'features' ? 'active' : ''} onClick={() => setActiveTab('features')}>Features</li>
            <li className={activeTab === 'users' ? 'active' : ''} onClick={() => setActiveTab('users')}>Users</li>
            <li className={activeTab === 'support' ? 'active' : ''} onClick={() => setActiveTab('support')}>Support Requests</li>
            <li className={activeTab === 'email' ? 'active' : ''} onClick={() => setActiveTab('email')}>Email Users</li>
            <li className={activeTab === 'sent' ? 'active' : ''} onClick={() => setActiveTab('sent')}>Sent Emails</li>
            <li className={activeTab === 'subscriptions' ? 'active' : ''} onClick={() => setActiveTab('subscriptions')}>Subscriptions</li>
          </ul>
        </nav>
        <button className="logout-btn" onClick={handleLogout}>Logout</button>
      </aside>
      <main className="dashboard-main">
        <h1>{
          activeTab === 'waitlist' ? 'Waitlist Submissions' :
          activeTab === 'newsletter' ? 'Newsletter Subscriptions' :
          activeTab === 'features' ? 'Requested Features' :
          activeTab === 'users' ? 'Users' :
          activeTab === 'support' ? 'Support Requests' :
          activeTab === 'email' ? 'Email Users' :
          activeTab === 'subscriptions' ? 'Subscription Management' :
          'Sent Emails'
        }</h1>
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            <span>Loading...</span>
          </div>
        ) : (
          activeTab === 'waitlist' ? renderWaitlist() :
          activeTab === 'newsletter' ? renderNewsletter() :
          activeTab === 'features' ? (
            <div className="data-table-container">
              {featureCounts.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">✨</div>
                  <div className="empty-state-text">No features submitted yet</div>
                  <div className="empty-state-subtext">Feature requests from waitlist entries will appear here</div>
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Feature</th>
                      <th>Request Count</th>
                      <th>Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {featureCounts.map(({ feature, count }) => {
                      const total = featureCounts.reduce((sum, f) => sum + f.count, 0);
                      const percentage = ((count / total) * 100).toFixed(1);
                      return (
                        <tr key={feature}>
                          <td><strong>{feature}</strong></td>
                          <td>
                            <span style={{color: 'var(--primary-color)', fontWeight: 600}}>{count}</span>
                          </td>
                          <td>
                            <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                              <div style={{
                                flex: 1,
                                height: '8px',
                                background: 'var(--outline-color)',
                                borderRadius: '4px',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  height: '100%',
                                  width: `${percentage}%`,
                                  background: 'var(--primary-color)',
                                  borderRadius: '4px',
                                  transition: 'width 0.3s ease'
                                }}></div>
                              </div>
                              <span style={{color: 'var(--on-surface-variant)', fontSize: '0.9rem', minWidth: '45px'}}>{percentage}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          ) : activeTab === 'users' ? renderUsers() :
          activeTab === 'support' ? renderSupportRequests() :
          activeTab === 'email' ? renderEmailUsers() :
          activeTab === 'subscriptions' ? renderSubscriptions() :
          renderSentEmails()
        )}
      </main>
      {/* User Subscription Modal */}
      <UserSubscriptionModal
        user={searchedUser}
        isOpen={!!searchedUser}
        onClose={() => {
          setSearchedUser(null);
        }}
        onUpdatePremium={updateUserPremium}
        updatingPremium={updatingPremium}
      />

      {selectedUser && (
        <div className="user-detail-modal" onClick={() => setSelectedUser(null)}>
          <div className="user-detail-content" onClick={e => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setSelectedUser(null)}>&times;</button>
            <h2 style={{marginBottom: 12}}>
              {selectedUser.fullName ||
                `${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim()}
            </h2>
            <div style={{marginBottom: 8, color: 'var(--on-surface-variant)'}}>User ID: {selectedUser.id}</div>
            <div className="user-details">
              <div className="user-detail-item">
                <div className="user-detail-label">Email</div>
                <div className="user-detail-value">{selectedUser.email}</div>
              </div>
              <div className="user-detail-item">
                <div className="user-detail-label">Gender</div>
                <div className="user-detail-value">{selectedUser.gender}</div>
              </div>
              <div className="user-detail-item">
                <div className="user-detail-label">Date of Birth</div>
                <div className="user-detail-value">{selectedUser.dob}</div>
              </div>
              <div className="user-detail-item">
                <div className="user-detail-label">Country</div>
                <div className="user-detail-value">{selectedUser.country}</div>
              </div>
              <div className="user-detail-item">
                <div className="user-detail-label">Phone</div>
                <div className="user-detail-value">
                  {selectedUser.countryCode
                    ? `${selectedUser.countryCode} ${selectedUser.phone}`
                    : selectedUser.phone}
                </div>
              </div>
              {/* Add more fields as needed */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 